import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import i18next from 'i18next';
import CONDITIONS from '../assets/conditions';
import { dynamicSortWithTraduction } from '../utils/sort';
import logo from '../assets/Images/logo150.png';
import '../styles/shareCharacter.css';

const db = firebase.firestore();

const HpBoxes = ({ max }) => {
  const display = Math.min(max, 40);
  return (
    <div className="sheetHpBoxes">
      {Array.from({ length: display }).map((_, i) => (
        <div key={i} className="sheetHpBox" />
      ))}
      {max > 40 && <span className="sheetHpOverflow">…/{max}</span>}
    </div>
  );
};

const ShareCharacter = () => {
  const { characterUid } = useParams();
  const [character, setCharacter] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    db.collection('characters').doc(characterUid).get()
      .then(snap => {
        if (snap.exists) setCharacter(snap.data());
        else setNotFound(true);
      })
      .catch(() => setNotFound(true));
  }, [characterUid]);

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      window.prompt('Copie ce lien :', window.location.href);
    }
  };

  if (notFound) return (
    <div className="sharePageWrapper shareNotFound"><p>Personnage introuvable.</p></div>
  );
  if (!character) return (
    <div className="sharePageWrapper shareLoading"><div className="shareSpinner" /></div>
  );

  const posMap = { top: '20%', center: '50%', bottom: '80%' };

  const characteristics = (character.characteristics || [])
    .slice().sort(dynamicSortWithTraduction('label', 'characteristics'));

  const skills = (character.skills || [])
    .filter(s => !s.isSpecial)
    .slice().sort(dynamicSortWithTraduction('label', 'skills'));
  const half = Math.ceil(skills.length / 2);

  const activeConditions = (character.conditions || [])
    .map(key => CONDITIONS.find(c => c.key === key)).filter(Boolean);

  const deathMagicSkill = (character.skills || [])
    .find(s => s.label === 'deathMagic' && !s.isCustom);

  const deathSpells = [1,2,3,4,5,6,7].map(n => ({
    name: i18next.t(`deathMagic.spellName${n}`),
    desc: i18next.t(`deathMagic.spellDesc${n}`),
  }));

  const alchemyReceipts = character.isAlchemist
    ? (character.alchemy?.receipt || []) : [];

  const magicCards = character.isMage ? (character.magicCards || []) : [];
  const cardsAvailable = magicCards.filter(c => c.enable);
  const cardsUsed = magicCards.filter(c => !c.enable);

  return (
    <div className="sharePageWrapper">

      {/* ── Toolbar ── */}
      <div className="shareToolbar">
        <div className="shareToolbarLeft">
          <img src={logo} alt="Aria" className="shareToolbarLogo" />
          <span className="shareToolbarTitle">Pray The Dice</span>
        </div>
        <div className="shareToolbarRight">
          <button className="shareToolBtn" onClick={handleCopy}>
            {copied ? '✓ Lien copié' : '🔗 Copier le lien'}
          </button>
          <button className="shareToolBtn shareToolBtnPrimary" onClick={() => window.print()}>
            🖨 Imprimer / PDF
          </button>
        </div>
      </div>

      {/* ── Fiche ── */}
      <div className="sheetPage">

        {/* En-tête */}
        <div className="sheetHeader">
          <div className="sheetHeaderLeft">
            {character.picture
              ? <img src={character.picture} alt={character.name} className="sheetPortrait"
                  style={{ objectPosition: `center ${posMap[character.picturePosition || 'center']}` }} />
              : <div className="sheetPortraitPlaceholder" />
            }
          </div>
          <div className="sheetHeaderCenter">
            <div className="sheetBrand">
              <img src={logo} alt="Aria" className="sheetBrandLogo" />
              <span>Pray The Dice — Fiche de personnage</span>
            </div>
            <h1 className="sheetCharName">{character.name}</h1>
            <div className="sheetHpSection">
              <div className="sheetHpLabel">
                <span>Points de vie</span>
                <strong>{character.maxHp} PV</strong>
              </div>
              <HpBoxes max={character.maxHp} />
            </div>
          </div>
        </div>

        <div className="sheetDivider" />

        <div className="sheetContent">

        {/* Corps : carac + compétences */}
        <div className="sheetBody">
          <div className="sheetCol sheetColLeft">
            <h2 className="sheetSectionTitle">{i18next.t('characteristic')}</h2>
            <div className="sheetCharacGrid">
              {characteristics.map((c, i) => (
                <div key={i} className="sheetCharacRow">
                  <span className="sheetCharacName">{i18next.t(`characteristics.${c.label}`)}</span>
                  <span className="sheetCharacBase">{c.value}</span>
                  <span className="sheetCharacFull">{c.value * 5}</span>
                </div>
              ))}
            </div>
            {character.description && (
              <>
                <h2 className="sheetSectionTitle" style={{ marginTop: '1.2rem' }}>Description</h2>
                <p className="sheetDescription">{character.description}</p>
              </>
            )}
          </div>

          <div className="sheetCol sheetColRight">
            <h2 className="sheetSectionTitle">{i18next.t('skill')}</h2>
            <div className="sheetSkillsColumns">
              <div className="sheetSkillsCol">
                {skills.slice(0, half).map((s, i) => (
                  <div key={i} className="sheetSkillRow">
                    <span className="sheetSkillName">{s.isCustom ? s.label : i18next.t(`skills.${s.label}`)}</span>
                    {s.note && <span className="sheetSkillNote">{s.note}</span>}
                    <span className="sheetSkillValue">{s.value}</span>
                  </div>
                ))}
              </div>
              <div className="sheetSkillsCol">
                {skills.slice(half).map((s, i) => (
                  <div key={i} className="sheetSkillRow">
                    <span className="sheetSkillName">{s.isCustom ? s.label : i18next.t(`skills.${s.label}`)}</span>
                    {s.note && <span className="sheetSkillNote">{s.note}</span>}
                    <span className="sheetSkillValue">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Magie */}
        {character.isMage && magicCards.length > 0 && (
          <>
            <div className="sheetDivider" />
            <div className="sheetExtraSection">
              <h2 className="sheetSectionTitle">{i18next.t('mage.title')}</h2>
              <div className="sheetMagicGroups">
                {cardsAvailable.length > 0 && (
                  <div className="sheetMagicGroup">
                    <span className="sheetMagicGroupLabel sheetMagicAvailable">{i18next.t('mage.cardsAvailable')}</span>
                    <div className="sheetCardGrid">
                      {cardsAvailable.map((card, i) => (
                        <div key={i} className="sheetCard sheetCardAvailable">
                          <span className={/♥|♦/.test(card.value) ? 'sheetCardRed' : 'sheetCardBlack'}>
                            {card.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {cardsUsed.length > 0 && (
                  <div className="sheetMagicGroup">
                    <span className="sheetMagicGroupLabel sheetMagicUsed">{i18next.t('mage.cardsUsed')}</span>
                    <div className="sheetCardGrid">
                      {cardsUsed.map((card, i) => (
                        <div key={i} className="sheetCard sheetCardUsed">
                          <span className={/♥|♦/.test(card.value) ? 'sheetCardRed' : 'sheetCardBlack'}>
                            {card.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Alchimie */}
        {character.isAlchemist && alchemyReceipts.length > 0 && (
          <>
            <div className="sheetDivider" />
            <div className="sheetExtraSection">
              <h2 className="sheetSectionTitle">{i18next.t('alchemy.title')}</h2>
              <div className="sheetAlchemyGrid">
                {alchemyReceipts.map((r, i) => (
                  <div key={i} className="sheetAlchemyRow">
                    <span className="sheetAlchemyName">
                      {r.default ? i18next.t(`alchemy.${r.name}`) : r.name}
                    </span>
                    <span className="sheetAlchemyDifficulty">{r.difficulty}%</span>
                    {r.description && !r.default && (
                      <span className="sheetAlchemyDesc">{r.description}</span>
                    )}
                    {r.default && (
                      <span className="sheetAlchemyDesc">
                        {i18next.t(`alchemy.${r.description}`)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Magie de la mort */}
        {character.isDeathMage && deathMagicSkill && (
          <>
            <div className="sheetDivider" />
            <div className="sheetExtraSection">
              <div className="sheetDeathMagicHeader">
                <h2 className="sheetSectionTitle">{i18next.t('deathMagic.title')}</h2>
                <span className="sheetDeathMagicSkill">
                  {i18next.t('skills.deathMagic')} : <span className="sheetWriteIn" />
                </span>
              </div>
              <div className="sheetSpellGrid">
                {deathSpells.map((spell, i) => (
                  <div key={i} className="sheetSpellRow">
                    <span className="sheetSpellName">{spell.name}</span>
                    <span className="sheetSpellDesc">{spell.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        </div>{/* end sheetContent */}

        {/* Pied de page */}
        <div className="sheetFooter">
          <span>praythedice.com</span>
          <span>{character.name}</span>
          <span>Imprimé le {new Date().toLocaleDateString('fr-FR')}</span>
        </div>

      </div>
    </div>
  );
};

export default ShareCharacter;
